import React, { useState, useEffect } from 'react';
import i18next from 'core/i18n';

import { DialogContent, Input, palettes, useFlashMessage } from '@indigo-ag/component-module';
import { Credit } from '@indigo-ag/schema';

import { CURRENCY_SYMBOL, EnhancedDialog, IIncomeStatementInfo, InputNumberMask, ModalActionButtons } from 'shared';
import { currency } from 'shared/utils';
import { IncomeStatementSectionProps } from '../../IncomeStatementSection';

export interface IncomeStatementModalProps extends IncomeStatementSectionProps {
  closeModal: () => void;
  open: boolean;
  // TODO create type for function
  updateCredit: (newCreditInfo: Partial<Credit>) => Promise<unknown>;
}

const initialStatementInfo: IIncomeStatementInfo = {
  returnOfIncomeCosting: '0',
  returnOfIncomeDebts: '0',
  returnOfIncomeGrossRevenue: '0',
  returnOfIncomeInvestment: '0',
  returnOfIncomePatrimony: '0',
  returnOfIncomeResult: '0',
  returnOfIncomeRural: '0',
  returnOfIncomeTaxableIncome: '0',
  returnOfIncomeUrban: '0',
  returnOfIncomeYearIRPF: '',
  returnOfIncomeExploredAreaCode: '',
  returnOfIncomeExploredHectares: 0
};

const applyRemoveInputMask = (incomeStatement: IIncomeStatementInfo, apply: boolean = false) => {
  const newIncomeStatementValues: IIncomeStatementInfo = { ...incomeStatement };

  Object.entries(incomeStatement).forEach(([key, value]) => {
    value = value ? value.toString() : value;
    if (
      key !== 'returnOfIncomeYearIRPF' &&
      key !== 'returnOfIncomeExploredAreaCode' &&
      key !== 'returnOfIncomeExploredHectares'
    )
      return (newIncomeStatementValues[key] = apply
        ? currency.inputMaskCurrency(value, { allowNegative: key === 'returnOfIncomeResult' })
        : value.replace(/\./g, ''));
  });
  return newIncomeStatementValues;
};

export const IncomeStatementModal: React.FunctionComponent<IncomeStatementModalProps> = ({
  closeModal,
  open,
  incomeStatementInfo,
  updateCredit
}) => {
  /**
   * State
   */
  const [newIncomeStatementInfo, setNewIncomeStatementInfo] = useState<IIncomeStatementInfo>(
    applyRemoveInputMask(incomeStatementInfo || initialStatementInfo, true)
  );

  const onCleanIncomeStatementSpace = (): void => {
    setNewIncomeStatementInfo(applyRemoveInputMask(incomeStatementInfo || initialStatementInfo, true));
  };

  useEffect(() => {
    onCleanIncomeStatementSpace();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateIncomeStatementInfo = (prop: keyof IIncomeStatementInfo) => {
    return (event: React.ChangeEvent<HTMLInputElement>): void => {
      let newValue = event.target.value;
      if (prop !== 'returnOfIncomeYearIRPF' && prop !== 'returnOfIncomeExploredAreaCode') {
        newValue = currency.inputMaskCurrency(event.target.value, { allowNegative: prop === 'returnOfIncomeResult' });
      }
      if (prop === 'returnOfIncomeExploredAreaCode') {
        newValue = /^[0-9]*$/i.test(newValue) ? newValue : `${newValue}`.replace(/\D+/g, '');
      }
      setNewIncomeStatementInfo({
        ...newIncomeStatementInfo,
        [prop]: newValue
      });
    };
  };

  const updateIncomeStatementNumberInfo = (prop: keyof IIncomeStatementInfo) => (value: number | null): void => {
    setNewIncomeStatementInfo({
      ...newIncomeStatementInfo,
      [prop]: value || 0
    });
  };

  const { addFlashMessage } = useFlashMessage();

  const updateInfo = () =>
    updateCredit(applyRemoveInputMask(newIncomeStatementInfo)).then(() => {
      addFlashMessage({
        content: `${i18next.t('incomeTax')} ${i18next.t('hasBeenUpdatedSuccessfully')}`,
        title: '',
        variant: 'success'
      });
    });

  const renderBrazilCurrencySymbol = (): JSX.Element => (
    <span style={{ color: palettes.neutral['900'] }}>{CURRENCY_SYMBOL.BR}</span>
  );

  return (
    <EnhancedDialog open={open} onClose={closeModal} title={i18next.t('incomeTax')}>
      <DialogContent>
        <Input
          label={i18next.t('yearIrpf')}
          onChange={updateIncomeStatementInfo('returnOfIncomeYearIRPF')}
          value={newIncomeStatementInfo.returnOfIncomeYearIRPF}
          variant="text"
        />
        <Input
          label={`${i18next.t('patrimony')} (${i18next.t('optional')})`}
          onChange={updateIncomeStatementInfo('returnOfIncomePatrimony')}
          renderBeforeInput={renderBrazilCurrencySymbol}
          value={newIncomeStatementInfo.returnOfIncomePatrimony}
          variant="text"
        />
        <Input
          label={`${i18next.t('rural')} (${i18next.t('optional')})`}
          onChange={updateIncomeStatementInfo('returnOfIncomeRural')}
          renderBeforeInput={renderBrazilCurrencySymbol}
          value={newIncomeStatementInfo.returnOfIncomeRural}
          variant="text"
        />
        <Input
          label={`${i18next.t('urban')} (${i18next.t('optional')})`}
          onChange={updateIncomeStatementInfo('returnOfIncomeUrban')}
          renderBeforeInput={renderBrazilCurrencySymbol}
          value={newIncomeStatementInfo.returnOfIncomeUrban}
          variant="text"
        />
        <Input
          label={`${i18next.t('investments')} (${i18next.t('optional')})`}
          onChange={updateIncomeStatementInfo('returnOfIncomeInvestment')}
          renderBeforeInput={renderBrazilCurrencySymbol}
          value={newIncomeStatementInfo.returnOfIncomeInvestment}
          variant="text"
        />
        <Input
          label={`${i18next.t('debtsAndLiens')} (${i18next.t('optional')})`}
          onChange={updateIncomeStatementInfo('returnOfIncomeDebts')}
          renderBeforeInput={renderBrazilCurrencySymbol}
          value={newIncomeStatementInfo.returnOfIncomeDebts}
          variant="text"
        />
        <Input
          label={`${i18next.t('grossRevenue')} (${i18next.t('optional')})`}
          onChange={updateIncomeStatementInfo('returnOfIncomeGrossRevenue')}
          renderBeforeInput={renderBrazilCurrencySymbol}
          value={newIncomeStatementInfo.returnOfIncomeGrossRevenue}
          variant="text"
        />
        <Input
          label={`${i18next.t('defrayal')} (${i18next.t('optional')})`}
          onChange={updateIncomeStatementInfo('returnOfIncomeCosting')}
          renderBeforeInput={renderBrazilCurrencySymbol}
          value={newIncomeStatementInfo.returnOfIncomeCosting}
          variant="text"
        />
        <Input
          label={`${i18next.t('result')} (${i18next.t('optional')})`}
          onChange={updateIncomeStatementInfo('returnOfIncomeResult')}
          renderBeforeInput={renderBrazilCurrencySymbol}
          value={newIncomeStatementInfo.returnOfIncomeResult}
          variant="text"
        />
        <Input
          label={`${i18next.t('taxableIncome')} (${i18next.t('optional')})`}
          onChange={updateIncomeStatementInfo('returnOfIncomeTaxableIncome')}
          renderBeforeInput={renderBrazilCurrencySymbol}
          value={newIncomeStatementInfo.returnOfIncomeTaxableIncome}
          variant="text"
        />
        <Input
          label={`${i18next.t('exploredAreaCode')} (${i18next.t('optional')})`}
          onChange={updateIncomeStatementInfo('returnOfIncomeExploredAreaCode')}
          value={newIncomeStatementInfo.returnOfIncomeExploredAreaCode}
          variant="text"
        />
        <InputNumberMask
          label={`${i18next.t('hectaresExplored')} (${i18next.t('optional')})`}
          name="hectaresExplored"
          onNumberChange={updateIncomeStatementNumberInfo('returnOfIncomeExploredHectares')}
          numberValue={Number(newIncomeStatementInfo.returnOfIncomeExploredHectares) || 0}
        />
      </DialogContent>
      <ModalActionButtons
        changeButtonName={i18next.t('saveChanges')}
        closeModal={closeModal}
        disabled={!newIncomeStatementInfo.returnOfIncomeYearIRPF}
        save={updateInfo}
      />
    </EnhancedDialog>
  );
};
